// src/pages/Users/PassageCeintureDocuments/components/PassageCeintureDocumentSheet.jsx

import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaExternalLinkAlt,
  FaFilePdf,
  FaImage,
  FaSave,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  addPassageCeintureDocument,
  updatePassageCeintureDocument,
  uploadPassageCeintureImage,
  uploadPassageCeinturePdf,
  deletePassageCeintureImage,
  deletePassageCeinturePdf,
} from "../../../../services/passageCeintureDocumentsServices";

import {
  beltLabel,
  beltTheme,
  isQuillEmpty,
  normalizePlainText,
  normalizeRichText,
  toAbsoluteUrl,
} from "../utils/passageCeintureDocumentsUtils";

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

export default function PassageCeintureDocumentSheet({
  isOpen,
  mode = "view",
  readOnly = true,
  document,
  belts = [],
  canManage = false,
  onClose,
  onEdit,
  onSaved,
}) {
  const isCreate = mode === "create";
  const isEdit = mode === "edit" && !!document?.id;
  const isForm = !readOnly;

  const [formData, setFormData] = useState({
    titre: "",
    description: "",
    ceinture: "",
    image_url: "",
    pdf_url: "",
    ordre_affichage: 0,
    is_published: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const theme = beltTheme(document?.ceinture || formData.ceinture);

  const imageUrl = toAbsoluteUrl(
    isForm ? formData.image_url : document?.image_url
  );

  const pdfUrl = toAbsoluteUrl(isForm ? formData.pdf_url : document?.pdf_url);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = documentBodyOverflow();

    window.document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      titre: document?.titre || "",
      description: document?.description || "",
      ceinture: document?.ceinture || "",
      image_url: document?.image_url || "",
      pdf_url: document?.pdf_url || "",
      ordre_affichage: document?.ordre_affichage ?? 0,
      is_published:
        document?.is_published === undefined ? true : !!document.is_published,
    });

    setImageFile(null);
    setPdfFile(null);
    setSubmitting(false);
  }, [isOpen, document]);

  if (!isOpen) return null;

  const title = readOnly
    ? document?.titre || "Document"
    : isCreate
    ? "Ajouter un document"
    : "Modifier le document";

  const subtitle = readOnly
    ? "Document de préparation au passage de ceinture"
    : "Image, PDF et paramètres d’affichage par ceinture.";

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!normalizePlainText(formData.titre)) {
      toast.error("Le titre est obligatoire.");
      return;
    }

    if (!normalizePlainText(formData.ceinture)) {
      toast.error("La ceinture est obligatoire.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        titre: normalizePlainText(formData.titre),
        description: normalizeRichText(formData.description) || null,
        ceinture: normalizePlainText(formData.ceinture),
        image_url: normalizePlainText(formData.image_url) || null,
        pdf_url: normalizePlainText(formData.pdf_url) || null,
        ordre_affichage: Number(formData.ordre_affichage) || 0,
        is_published: !!formData.is_published,
      };

      let savedDocument;

      if (isEdit) {
        savedDocument = await updatePassageCeintureDocument(document.id, payload);
      } else {
        savedDocument = await addPassageCeintureDocument(payload);
      }

      const targetId = savedDocument?.id || document?.id;

      if (imageFile && targetId) {
        await uploadPassageCeintureImage(targetId, imageFile);
      }

      if (pdfFile && targetId) {
        await uploadPassageCeinturePdf(targetId, pdfFile);
      }

      toast.success(isEdit ? "Document mis à jour" : "Document créé");
      await onSaved?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!document?.id) return;

    try {
      await deletePassageCeintureImage(document.id);

      setFormData((prev) => ({
        ...prev,
        image_url: "",
      }));

      toast.success("Image supprimée");
      await onSaved?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression de l'image");
    }
  };

  const handleDeletePdf = async () => {
    if (!document?.id) return;

    try {
      await deletePassageCeinturePdf(document.id);

      setFormData((prev) => ({
        ...prev,
        pdf_url: "",
      }));

      toast.success("PDF supprimé");
      await onSaved?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression du PDF");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-950/65 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <article
        className="absolute inset-x-0 bottom-0 max-h-[94dvh] overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-900 sm:relative sm:inset-auto sm:w-full sm:max-w-5xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header
          className={`relative overflow-hidden bg-gradient-to-r ${theme.gradient} px-4 py-4 text-white sm:px-6`}
        >
          <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/20 blur-2xl" />

          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase ring-1 ${theme.badge}`}
              >
                {document?.ceinture || formData.ceinture || "Ceinture"}
              </span>

              <h2 className="mt-2 line-clamp-2 text-xl font-bold leading-tight sm:text-2xl">
                {title}
              </h2>

              <p className="mt-1 text-sm text-white/85">{subtitle}</p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {readOnly && canManage && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
                  aria-label="Modifier"
                >
                  <FaEdit />
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </header>

        {readOnly ? (
          <ReadOnlyContent
            document={document}
            imageUrl={imageUrl}
            pdfUrl={pdfUrl}
          />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="max-h-[calc(94dvh-128px)] overflow-y-auto p-4 sm:p-6"
          >
            <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
              <div className="space-y-4">
                <Field label="Titre *">
                  <input
                    type="text"
                    name="titre"
                    value={formData.titre}
                    onChange={handleChange}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </Field>

                <Field label="Ceinture *">
                  <select
                    name="ceinture"
                    value={formData.ceinture}
                    onChange={handleChange}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">Sélectionner une ceinture</option>
                    {belts.map((belt, index) => {
                      const label = (beltLabel(belt) || "").trim();

                      if (!label) return null;

                      return (
                        <option key={`${label}-${index}`} value={label}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </Field>

                <Field label="Description">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: value,
                        }))
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Saisir une description enrichie..."
                      className="bg-white text-slate-900 dark:bg-slate-950 dark:text-white"
                    />
                  </div>
                </Field>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Ordre d’affichage">
                    <input
                      type="number"
                      min="0"
                      name="ordre_affichage"
                      value={formData.ordre_affichage}
                      onChange={handleChange}
                      className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </Field>

                  <div className="flex items-end">
                    <label className="flex h-12 w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                      <span>Publié</span>

                      <input
                        type="checkbox"
                        name="is_published"
                        checked={!!formData.is_published}
                        onChange={handleChange}
                        className="h-5 w-5"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <MediaBox
                  icon={FaImage}
                  title="Image"
                  urlValue={formData.image_url}
                  urlName="image_url"
                  onChange={handleChange}
                  placeholder="URL image externe"
                  accept="image/png,image/jpeg,image/webp"
                  selectedFile={imageFile}
                  onFileChange={setImageFile}
                  currentUrl={imageUrl}
                  onDelete={isEdit && document?.image_url ? handleDeleteImage : null}
                />

                <MediaBox
                  icon={FaFilePdf}
                  title="PDF"
                  urlValue={formData.pdf_url}
                  urlName="pdf_url"
                  onChange={handleChange}
                  placeholder="URL PDF externe"
                  accept="application/pdf"
                  selectedFile={pdfFile}
                  onFileChange={setPdfFile}
                  currentUrl={pdfUrl}
                  onDelete={isEdit && document?.pdf_url ? handleDeletePdf : null}
                />
              </div>
            </div>

            <div className="sticky bottom-0 -mx-4 mt-6 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:-mx-6 sm:px-6">
              <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                >
                  <FaTimes />
                  Fermer
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-bold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
                >
                  <FaSave />
                  {submitting ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </form>
        )}
      </article>
    </div>
  );
}

function ReadOnlyContent({ document, imageUrl, pdfUrl }) {
  const hasDescription = !isQuillEmpty(document?.description);

  return (
    <div className="max-h-[calc(94dvh-128px)] overflow-y-auto p-4 sm:p-6">
      {imageUrl && (
        <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
          <img
            src={imageUrl}
            alt={document?.titre || "Document"}
            className="max-h-[520px] w-full object-contain"
          />
        </div>
      )}

      {!imageUrl && (
        <div className="flex min-h-[220px] items-center justify-center rounded-[1.5rem] border border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-800 dark:bg-slate-950">
          <FaImage className="text-5xl" />
        </div>
      )}

      {hasDescription && (
        <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Description
          </p>

          <div
            className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300
            [&_p]:mb-2 [&_p:last-child]:mb-0
            [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5
            [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5
            [&_strong]:font-bold [&_em]:italic
            [&_a]:text-blue-600 [&_a]:underline dark:[&_a]:text-blue-400"
            dangerouslySetInnerHTML={{ __html: document.description }}
          />
        </div>
      )}

      <div className="sticky bottom-0 -mx-4 mt-5 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:-mx-6 sm:px-6">
        <div className="grid grid-cols-2 gap-3">
          {imageUrl ? (
            <a
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaImage />
              Image
            </a>
          ) : (
            <span className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-400 dark:bg-slate-800">
              <FaImage />
              Image
            </span>
          )}

          {pdfUrl ? (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-bold text-white transition active:scale-95"
            >
              <FaFilePdf />
              Ouvrir le PDF
            </a>
          ) : (
            <span className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white dark:bg-white dark:text-slate-950">
              <FaExternalLinkAlt />
              Consulter
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      {children}
    </label>
  );
}

function MediaBox({
  icon: Icon,
  title,
  urlValue,
  urlName,
  onChange,
  placeholder,
  accept,
  selectedFile,
  onFileChange,
  currentUrl,
  onDelete,
}) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
        <Icon />
        {title}
      </div>

      <input
        type="text"
        name={urlName}
        value={urlValue}
        onChange={onChange}
        placeholder={placeholder}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
      />

      <label className="mt-3 flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 text-sm font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800">
        <FaUpload />
        {selectedFile ? selectedFile.name : "Choisir un fichier"}

        <input
          type="file"
          accept={accept}
          onChange={(event) => onFileChange(event.target.files?.[0] || null)}
          className="hidden"
        />
      </label>

      {currentUrl && (
        <a
          href={currentUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
        >
          <FaExternalLinkAlt />
          Voir le fichier actuel
        </a>
      )}

      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-xs font-bold text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
        >
          <FaTrash />
          Supprimer le fichier
        </button>
      )}
    </div>
  );
}

function documentBodyOverflow() {
  if (typeof window === "undefined") return "";
  return window.document.body.style.overflow;
}